import type { StatusTransMap } from "components/table";
import { OrderDebtFilter, OrderDebtStatus, OrderStatus, type OrderCounts, type OrderDebt } from "types/order";
import dayjs from "utils/dayjs";
import type { BadgeTone } from "views/Opportunity/opportunity.meta";

export const ORDER_PATH = "/order";

export const ORDER_UPLOAD_URL = `${import.meta.env.VITE_API_URL}/v1/admin/order/upload`;

export const OTHER_DOCUMENT_LABEL = "Loại khác";

export const documentLabelPresets = [
  "Hồ sơ yêu cầu",
  "Hợp đồng",
  "Phụ lục",
  "Đề nghị thanh toán",
  "Hóa đơn",
  "Biên bản xác nhận công việc",
  "Biên bản nghiệm thu",
  "Biên bản thanh lý",
];

export const orderStatusLabel: Record<OrderStatus, string> = {
  [OrderStatus.Kickoff]: "Đã kickoff",
  [OrderStatus.Developing]: "Đang phát triển",
  [OrderStatus.Acceptance]: "Nghiệm thu",
  [OrderStatus.Handover]: "Bàn giao",
  [OrderStatus.Collecting]: "Thu hồi công nợ",
  [OrderStatus.Done]: "Đóng",
};

export const orderStatusTrans: StatusTransMap<OrderStatus> = {
  [OrderStatus.Kickoff]: { label: "Đã kickoff", color: "#175cd3", bgcolor: "#d1e9ff" },
  [OrderStatus.Developing]: { label: "Đang phát triển", color: "#b54708", bgcolor: "#fffaeb" },
  [OrderStatus.Acceptance]: { label: "Nghiệm thu", color: "#a15c07", bgcolor: "#fef7c3" },
  [OrderStatus.Handover]: { label: "Bàn giao", color: "#5925dc", bgcolor: "#ebe9fe" },
  [OrderStatus.Collecting]: { label: "Thu hồi công nợ", color: "#b42318", bgcolor: "#fee4e2" },
  [OrderStatus.Done]: { label: "Đóng", color: "#027a48", bgcolor: "#d1fadf" },
};

export const orderTabs: { key: string; label: string; tone: BadgeTone }[] = [
  { key: "ALL", label: "Tất cả", tone: "red" },
  { key: OrderStatus.Kickoff, label: orderStatusLabel[OrderStatus.Kickoff], tone: "blue" },
  { key: OrderStatus.Developing, label: orderStatusLabel[OrderStatus.Developing], tone: "orange" },
  { key: OrderStatus.Acceptance, label: orderStatusLabel[OrderStatus.Acceptance], tone: "gold" },
  { key: OrderStatus.Handover, label: orderStatusLabel[OrderStatus.Handover], tone: "blue" },
  { key: OrderStatus.Collecting, label: orderStatusLabel[OrderStatus.Collecting], tone: "red" },
  { key: OrderStatus.Done, label: orderStatusLabel[OrderStatus.Done], tone: "green" },
];

export const orderStatusOptions = (Object.keys(orderStatusLabel) as OrderStatus[]).map((value) => ({
  value,
  label: orderStatusLabel[value],
}));

export const emptyOrderCounts = (): OrderCounts => ({
  all: 0,
  statuses: {
    [OrderStatus.Kickoff]: 0,
    [OrderStatus.Developing]: 0,
    [OrderStatus.Acceptance]: 0,
    [OrderStatus.Handover]: 0,
    [OrderStatus.Collecting]: 0,
    [OrderStatus.Done]: 0,
  },
});

export const amountFromPercent = (totalAmount: number, percent: number) =>
  Math.round((totalAmount * percent) / 100);

export const debtStatusLabel: Record<OrderDebtStatus, string> = {
  [OrderDebtStatus.Unpaid]: "Chưa thu",
  [OrderDebtStatus.Paid]: "Đã thu",
};

export const debtStatusOptions = (Object.keys(debtStatusLabel) as OrderDebtStatus[]).map((value) => ({
  value,
  label: debtStatusLabel[value],
}));

export const debtFilterOptions = [
  { value: OrderDebtFilter.Full, label: "Đã thu đủ" },
  { value: OrderDebtFilter.None, label: "Chưa thu" },
  { value: OrderDebtFilter.Partial, label: "Đã thu 1 phần" },
];

export const toDisplayDate = (value?: string) => {
  if (!value) return "";
  const [year, month, day] = value.split("-");
  if (!year || !month || !day) return value;
  return `${day}/${month}/${year}`;
};

export type CollectedTone = "danger" | "warning" | "success";

export const collectedAmountTone = (collected: number, orderAmount: number): CollectedTone => {
  if (collected === 0 && orderAmount > 0) return "danger";
  if (collected < orderAmount) return "warning";
  return "success";
};

export const isDebtOverdue = (orderDebt: Pick<OrderDebt, "status" | "dueDate">, today = dayjs().format("YYYY-MM-DD")) =>
  orderDebt.status !== OrderDebtStatus.Paid && !!orderDebt.dueDate && orderDebt.dueDate < today;
